<script setup lang="ts">
import { useHead } from '@unhead/vue';
import AppHeader from '@/components/layout/AppHeader.vue';
import AppFooter from '@/components/layout/AppFooter.vue';

useHead({
  title: 'How Spaced Repetition Works | LLM-SR',
  meta: [
    {
      name: 'description',
      content: 'Learn how spaced repetition works and why LLM-SR uses the FSRS algorithm. FSRS outperforms traditional methods in 99.6% of cases, meaning less review time with better retention.',
    },
    {
      property: 'og:title',
      content: 'How Spaced Repetition Works | LLM-SR',
    },
    {
      property: 'og:description',
      content: 'Learn how spaced repetition works and why LLM-SR uses the FSRS algorithm for optimal memory retention.',
    },
  ],
});
</script>

<template>
  <div class="min-h-screen bg-background flex flex-col">
    <AppHeader />

    <main class="flex-1 max-w-3xl mx-auto px-4 py-12">
      <h1 class="text-3xl font-bold text-foreground mb-8">How Spaced Repetition Works</h1>

      <article class="prose prose-neutral dark:prose-invert max-w-none">
        <!-- Why We Forget -->
        <section class="mb-12">
          <h2 class="text-2xl font-semibold text-foreground mb-4">Why We Forget</h2>
          <p class="text-muted-foreground mb-4">
            In 1885, psychologist Hermann Ebbinghaus discovered something uncomfortable: without reinforcement, we forget most of what we learn within 24 hours. His "forgetting curve" shows memory decaying exponentially—a steep drop at first, then a gradual fade.
          </p>
          <p class="text-muted-foreground">
            Traditional study methods fight this curve poorly. Re-reading notes, highlighting passages, and cramming before exams all feel productive but don't create lasting memories. The problem isn't effort—it's timing.
          </p>
        </section>

        <!-- The Spacing Effect -->
        <section class="mb-12">
          <h2 class="text-2xl font-semibold text-foreground mb-4">The Spacing Effect</h2>
          <p class="text-muted-foreground mb-4">
            Here's the key insight: each time you successfully recall information, your memory of it strengthens and decays more slowly. But there's a catch—the recall has to happen at the right moment, ideally just as you're about to forget.
          </p>
          <p class="text-muted-foreground mb-4">
            This is the <strong class="text-foreground">spacing effect</strong>, and it's one of the most robust findings in cognitive psychology. Review too early and you're wasting time. Review too late and you've already forgotten. Review at the sweet spot and each repetition dramatically extends how long you'll remember.
          </p>
          <p class="text-muted-foreground mb-4">
            Spaced repetition systems automate this timing. Instead of reviewing everything daily, you review each card precisely when it's most efficient to do so.
          </p>
          <p class="text-muted-foreground">
            The efficiency gains are substantial. Research suggests spaced repetition can reduce total review time by roughly <strong class="text-foreground">20x</strong> compared to conventional methods. A single flashcard, reviewed optimally over 20 years, requires only about 5 minutes of total practice time.
          </p>
        </section>

        <!-- How LLM-SR Schedules Your Reviews -->
        <section class="mb-12">
          <h2 class="text-2xl font-semibold text-foreground mb-4">How LLM-SR Schedules Your Reviews</h2>
          <p class="text-muted-foreground mb-4">
            Most flashcard apps use an algorithm called SM-2, developed in 1987 for SuperMemo. It's been remarkably influential—Anki, the most popular flashcard app, still uses it as the default. But SM-2 has known limitations:
          </p>
          <ul class="list-disc list-inside text-muted-foreground mb-6 space-y-2">
            <li><strong class="text-foreground">Static formulas</strong>: It uses the same intervals for everyone, ignoring individual memory differences</li>
            <li><strong class="text-foreground">Ease factor drift</strong>: Cards can get trapped in "ease hell," showing up too frequently</li>
            <li><strong class="text-foreground">No learning</strong>: The algorithm doesn't improve based on your actual performance</li>
          </ul>
          <p class="text-muted-foreground mb-4">
            LLM-SR uses <strong class="text-foreground">FSRS</strong> (Free Spaced Repetition Scheduler), a modern algorithm developed by analyzing over 700 million reviews. FSRS models memory with three components:
          </p>

          <!-- Memory Components Table -->
          <div class="overflow-x-auto mb-6">
            <table class="w-full border-collapse">
              <thead>
                <tr class="border-b border-border">
                  <th class="text-left py-3 pr-4 text-foreground font-semibold">Component</th>
                  <th class="text-left py-3 text-foreground font-semibold">What it measures</th>
                </tr>
              </thead>
              <tbody class="text-muted-foreground">
                <tr class="border-b border-border">
                  <td class="py-3 pr-4 font-medium text-foreground">Retrievability</td>
                  <td class="py-3">Probability you'll recall the card right now</td>
                </tr>
                <tr class="border-b border-border">
                  <td class="py-3 pr-4 font-medium text-foreground">Stability</td>
                  <td class="py-3">Days until recall probability drops to 90%</td>
                </tr>
                <tr class="border-b border-border">
                  <td class="py-3 pr-4 font-medium text-foreground">Difficulty</td>
                  <td class="py-3">How challenging this card is for you personally</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p class="text-muted-foreground">
            FSRS adapts to your memory patterns. It learns which types of material you retain easily and which need more reinforcement.
          </p>
        </section>

        <!-- FSRS vs SM-2 -->
        <section class="mb-12">
          <h2 class="text-2xl font-semibold text-foreground mb-4">FSRS vs SM-2: The Benchmarks</h2>
          <p class="text-muted-foreground mb-4">
            Independent benchmarks using 350 million reviews from 10,000 Anki users show FSRS outperforming SM-2 in <strong class="text-foreground">99.6% of comparisons</strong> (measured by log loss). Users switching to FSRS typically report <strong class="text-foreground">20-30% fewer daily reviews</strong> while maintaining the same retention rate.
          </p>
          <p class="text-muted-foreground">
            The practical difference: less time reviewing, same results.
          </p>
        </section>

        <!-- LLM-Managed Flashcards -->
        <section class="mb-12">
          <h2 class="text-2xl font-semibold text-foreground mb-4">LLM-Managed Flashcards</h2>
          <p class="text-muted-foreground mb-4">
            The unique aspect of LLM-SR is how cards are created and managed. Instead of manually writing flashcards—which research shows is a skill that takes time to develop—you can ask your AI assistant to create them from conversations, documents, or topics you're learning.
          </p>
          <p class="text-muted-foreground">
            Good flashcard design matters. Cards should be atomic (one concept each), precise, and connected to what you already know. LLMs can help apply these principles consistently, letting you focus on learning rather than card management.
          </p>
        </section>

        <!-- Resources -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-foreground mb-6">Resources</h2>
          <p class="text-muted-foreground mb-8">
            Curious to learn more about spaced repetition? These are some of the best articles on the topic:
          </p>

          <!-- Getting Started -->
          <h3 class="text-xl font-medium text-foreground mb-4">Getting Started</h3>
          <ul class="list-disc list-inside text-muted-foreground mb-8 space-y-2">
            <li>
              <a href="https://ncase.me/remember/" target="_blank" rel="noopener" class="text-primary hover:underline font-medium">How To Remember Anything Forever-ish</a>
              — An interactive comic that explains the forgetting curve and spaced repetition visually. The best introduction for beginners.
            </li>
          </ul>

          <!-- Writing Better Flashcards -->
          <h3 class="text-xl font-medium text-foreground mb-4">Writing Better Flashcards</h3>
          <ul class="list-disc list-inside text-muted-foreground mb-8 space-y-2">
            <li>
              <a href="https://andymatuschak.org/prompts/" target="_blank" rel="noopener" class="text-primary hover:underline font-medium">How to write good prompts</a>
              — Andy Matuschak's comprehensive guide to creating effective flashcards for different types of knowledge.
            </li>
            <li>
              <a href="https://www.supermemo.com/en/articles/20rules" target="_blank" rel="noopener" class="text-primary hover:underline font-medium">Effective learning: Twenty rules of formulating knowledge</a>
              — The classic SuperMemo guide to card design, covering minimum information principle, cloze deletions, and avoiding interference.
            </li>
            <li>
              <a href="https://controlaltbackspace.org/memory/designing-precise-cards/" target="_blank" rel="noopener" class="text-primary hover:underline font-medium">Rules for Designing Precise Anki Cards</a>
              — Practical patterns for writing cards that test exactly what you intend.
            </li>
          </ul>

          <!-- Deep Dives -->
          <h3 class="text-xl font-medium text-foreground mb-4">Deep Dives</h3>
          <ul class="list-disc list-inside text-muted-foreground mb-8 space-y-2">
            <li>
              <a href="http://augmentingcognition.com/ltm.html" target="_blank" rel="noopener" class="text-primary hover:underline font-medium">Augmenting Long-term Memory</a>
              — Michael Nielsen's exploration of using spaced repetition for research and deep learning, including his personal workflow.
            </li>
            <li>
              <a href="https://numinous.productions/ttft/" target="_blank" rel="noopener" class="text-primary hover:underline font-medium">How can we develop transformative tools for thought?</a>
              — Matuschak and Nielsen on the future of memory systems and cognitive tools.
            </li>
            <li>
              <a href="https://cognitivemedium.com/srs-mathematics" target="_blank" rel="noopener" class="text-primary hover:underline font-medium">Using spaced repetition systems to see through a piece of mathematics</a>
              — Nielsen on using spaced repetition for conceptual understanding, not just facts.
            </li>
          </ul>

          <!-- The Science -->
          <h3 class="text-xl font-medium text-foreground mb-4">The Science</h3>
          <ul class="list-disc list-inside text-muted-foreground mb-8 space-y-2">
            <li>
              <a href="https://notes.andymatuschak.org/Spaced_repetition_systems_can_be_used_to_program_attention" target="_blank" rel="noopener" class="text-primary hover:underline font-medium">Spaced repetition systems can be used to program attention</a>
              — On using spaced repetition beyond memorization.
            </li>
            <li>
              <a href="https://quantum.country/" target="_blank" rel="noopener" class="text-primary hover:underline font-medium">Quantum Country</a>
              — A "mnemonic medium" that embeds spaced repetition into an essay on quantum computing. Experience the technique while learning.
            </li>
          </ul>

          <!-- Algorithm Details -->
          <h3 class="text-xl font-medium text-foreground mb-4">Algorithm Details</h3>
          <ul class="list-disc list-inside text-muted-foreground space-y-2">
            <li>
              <a href="https://eshapard.github.io/anki/thoughts-on-a-new-algorithm-for-anki.html" target="_blank" rel="noopener" class="text-primary hover:underline font-medium">Thoughts On A New Algorithm For Anki</a>
              — Early critique of SM-2's limitations that helped inspire FSRS.
            </li>
            <li>
              <a href="https://github.com/open-spaced-repetition/fsrs-benchmark" target="_blank" rel="noopener" class="text-primary hover:underline font-medium">FSRS Benchmark</a>
              — The open-source benchmark comparing spaced repetition algorithms.
            </li>
          </ul>
        </section>
      </article>
    </main>

    <AppFooter />
  </div>
</template>
